import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ReligiousEducationList from './components/ReligiousEducationList';
import ParishRegistrationList from './components/ParishRegistrationList';
import StaffManagement from './pages/StaffManagement';
import ReligiousEducationSettings from './pages/ReligiousEducationSettings';
import ProgramsManagement from './pages/ProgramsManagement';
import PublicRegistration from './pages/PublicRegistration';
import PaymentLookup from './pages/PaymentLookup';
import OCIARegistration from './pages/OCIARegistration';
import OCIAPaymentLookup from './pages/OCIAPaymentLookup';
import OCIAList from './components/OCIAList';
import OCIAViewerPage from './pages/OCIAViewerPage';
import OCIASettings from './pages/OCIASettings';
import CampusMinistryRegistration from './pages/CampusMinistryRegistration';
import CampusMinistryPaymentLookup from './pages/CampusMinistryPaymentLookup';
import CampusMinistryList from './components/CampusMinistryList';
import CampusMinistryViewerPage from './pages/CampusMinistryViewerPage';
import CampusMinistrySettings from './pages/CampusMinistrySettings';
import ReligiousEducationViewerPage from './pages/ReligiousEducationViewerPage';
import ParishRegistrationViewerPage from './pages/ParishRegistrationViewerPage';
import ParishRegistrationSettings from './pages/ParishRegistrationSettings';
import MassManagement from './pages/MassManagement';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import PermissionRoute from './components/PermissionRoute';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { LoadingProvider } from './contexts/LoadingContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Wrapper that handles routing - public routes rendered outside AuthProvider
const AppContent = () => {
  return (
    <Routes>
      {/* Public routes - NO AuthProvider wrapper */}
      <Route path="/re/register" element={<PublicRegistration />} />
      <Route path="/re/payment" element={<PaymentLookup />} />
      <Route path="/ocia/register" element={<OCIARegistration />} />
      <Route path="/ocia/payment" element={<OCIAPaymentLookup />} />
      <Route path="/campus-ministry/:formType/register" element={<CampusMinistryRegistration />} />
      <Route path="/campus-ministry/:formType/payment" element={<CampusMinistryPaymentLookup />} />

      {/* All other routes wrapped in AuthProvider */}
      <Route path="/*" element={
        <AuthProvider>
          <AuthenticatedRoutes />
        </AuthProvider>
      } />
    </Routes>
  );
};

// Authenticated routes component
const AuthenticatedRoutes = () => {
  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        } />
        <Route path="/religious-education" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="ccdRegistrations">
              <ReligiousEducationList />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/religious-education/:registrationId" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="ccdRegistrations">
              <ReligiousEducationViewerPage />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/parish-registration" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="parishRegistrations">
              <ParishRegistrationList />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/parish-registration/:registrationId" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="parishRegistrations">
              <ParishRegistrationViewerPage />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/staff" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="staffManagement">
              <StaffManagement />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/religious-education/settings" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="ccdRegistrations">
              <ReligiousEducationSettings />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/religious-education/programs" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="ccdRegistrations">
              <ProgramsManagement />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/parish-registration/settings" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="parishRegistrations">
              <ParishRegistrationSettings />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/mass/*" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="massManagement">
              <MassManagement />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/ocia" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="ociaRegistrations">
              <OCIAList />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/ocia/settings" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="ociaRegistrations">
              <OCIASettings />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/ocia/:registrationId" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="ociaRegistrations">
              <OCIAViewerPage />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/campus-ministry" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="campusMinistryRegistrations">
              <CampusMinistryList />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/campus-ministry/settings" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="campusMinistryRegistrations">
              <CampusMinistrySettings />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="/campus-ministry/registrations/:registrationId" element={
          <PrivateRoute>
            <PermissionRoute requiredPermission="campusMinistryRegistrations">
              <CampusMinistryViewerPage />
            </PermissionRoute>
          </PrivateRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorProvider>
        <LoadingProvider>
          <Router>
            <AppContent />
          </Router>
        </LoadingProvider>
      </ErrorProvider>
    </ThemeProvider>
  );
}

export default App;
